import {
  getBanner,
  getInterfaces,
  getNodeConfigurations,
} from '../banner/getters';
import { getNestedField, setNestedField } from '../utils';
import {
  atLeast,
  email,
  oneLetter,
  oneNumber,
  oneSpecialCharacter,
  required,
} from '../validations';
import { dynamicField, visibility } from './utils';
import { CSSProperties } from 'react';
import { RootState } from '..';
import rootGetters from '../getters';
export type NodeGeometry = {
  width: number;
  height: number;
  top: number;
  left?: number;
  right?: number;
  textAlign: CSSProperties['textAlign'];
  margin: CSSProperties['margin'];
  color?: [number, number, number];
  rotate?: number;
  matrix: number[];
  background?: [number, number, number, number];
  size?: number;
  fontFamily?: CSSProperties['fontFamily'];
  fontWeight?: CSSProperties['fontWeight'];
  variant?: 'primary' | 'secondary';
  duplicity?: 'single' | 'double';
  alignItems?: CSSProperties['alignItems'];
  justifyContent?: CSSProperties['justifyContent'];
  text?: string | number;
  code?: string | number;
  borderRadius?: CSSProperties['borderRadius'];
};
export const getForm =
  (state: RootState) =>
  <T extends FlattenKeys<DataForm>>(field: T) =>
    getNestedField(state.form.data, field);

export const getBlurForm =
  (state: RootState) =>
  <T extends FlattenKeys<BlurForm>>(field: T) =>
    getNestedField(state.form.blur, field);

export const getBannerConfigurations =
  (state: RootState) => (screenWidth: number, form: SingleBannerForm) => {
    const { size, orientation } = form.model;
    const sizes = getBanner(state)('sizes');
    const { width, height } = sizes[size];
    const aspectRatio =
      orientation === 'portrait' ? height / width : width / height;
    const axisX = 1000;
    return {
      width: screenWidth,
      height: aspectRatio * screenWidth,
      axisX,
      axisY: aspectRatio * axisX,
    };
  };

export const getRecoverFormMessage = (state: RootState) => () => {
  const data = state.form.data.recoverPassword;
  const blur = state.form.blur.recoverPassword;

  const messages: Record<
    keyof RecoverPasswordBlurForm,
    { '0': string | boolean } & Record<string, string | boolean>
  > = {
    email: {
      '0': '',
    },
    password: {
      '0': false,
    },
    passwordConfirmation: {
      '0': false,
    },
  };

  messages.email['email'] = '';
  if (blur.email && !email(String(data.email))) {
    messages.email['email'] = 'Informe um email válido';
    messages.email['0'] = messages.email['email'];
  }

  messages.email['required'] = '';
  if (blur.email && !required(String(data.email))) {
    messages.email['required'] = 'É necessário informar o email';
    messages.email['0'] = messages.email['required'];
  }

  messages.password['atLeast'] = blur.password
    ? !atLeast(String(data.password), 6)
    : false;

  messages.password['0'] ||= messages.password['atLeast'];

  messages.password['oneLetter'] = blur.password
    ? !oneLetter(String(data.password))
    : false;
  messages.password['0'] ||= messages.password['oneLetter'];

  messages.password['oneNumber'] = blur.password
    ? !oneNumber(String(data.password))
    : false;
  messages.password['0'] ||= messages.password['oneNumber'];

  messages.password['oneSpecialCharacter'] = blur.password
    ? !oneSpecialCharacter(String(data.password))
    : false;
  messages.password['0'] ||= messages.password['oneSpecialCharacter'];

  messages.password['samePassword'] = blur.password
    ? !(data.password === data.passwordConfirmation)
    : false;
  messages.password['0'] ||= messages.password['samePassword'];

  messages.passwordConfirmation['atLeast'] = blur.passwordConfirmation
    ? !atLeast(String(data.passwordConfirmation), 6)
    : false;
  messages.passwordConfirmation['0'] ||=
    messages.passwordConfirmation['atLeast'];

  messages.passwordConfirmation['oneLetter'] = blur.passwordConfirmation
    ? !oneLetter(String(data.passwordConfirmation))
    : false;
  messages.passwordConfirmation['0'] ||=
    messages.passwordConfirmation['oneLetter'];

  messages.passwordConfirmation['oneNumber'] = blur.passwordConfirmation
    ? !oneNumber(String(data.passwordConfirmation))
    : false;
  messages.passwordConfirmation['0'] ||=
    messages.passwordConfirmation['oneNumber'];

  messages.passwordConfirmation['oneSpecialCharacter'] =
    blur.passwordConfirmation
      ? !oneSpecialCharacter(String(data.passwordConfirmation))
      : false;
  messages.passwordConfirmation['0'] ||=
    messages.passwordConfirmation['oneSpecialCharacter'];

  messages.passwordConfirmation['samePassword'] = blur.passwordConfirmation
    ? !(data.passwordConfirmation === data.password)
    : false;
  messages.passwordConfirmation['0'] ||=
    messages.passwordConfirmation['samePassword'];

  return messages;
};

export const getTransformations =
  (state: RootState) =>
  (
    screenWidth: number,
    form: SingleBannerForm,
    format: 'html' | 'pdf' = 'html',
  ) => {
    const { size, dynamic, orientation, layout, campaign } = form.model;
    const { axisX, axisY, width, height } = getBannerConfigurations(state)(
      screenWidth,
      form,
    );
    const { globals, modifiers, complements, geometries } =
      getNodeConfigurations(state)();
    const relativeWidth = width / axisX;
    const defaultTransformMatrix = [relativeWidth, 0, 0, relativeWidth, 0, 0];
    return (
      geometries.find(
        (geo) =>
          geo.dynamic === dynamic &&
          geo.sizes.includes(size) &&
          geo.orientation === orientation &&
          geo.layouts?.includes(layout) &&
          geo.campaigns?.includes(campaign),
      )?.children ??
      geometries.find(
        (geo) =>
          geo.dynamic === dynamic &&
          geo.sizes.includes(size) &&
          geo.orientation === orientation,
      )?.children ??
      []
    )
      .map((node) => {
        const globalizedNode = {
          ...globals,
          ...node,
        };
        const nodeComplements = complements.reduce(
          (acc: any, { type, ...acv }: any) =>
            (Array.isArray(type) ? type : [type]).includes(globalizedNode.type)
              ? {
                  ...acc,
                  ...acv,
                }
              : acc,
          {},
        );

        const complementedNode = {
          ...nodeComplements,
          ...globalizedNode,
          ...(() =>
            globalizedNode.modifiers && nodeComplements.modifiers
              ? {
                  modifiers: [
                    ...new Set([
                      ...(Array.isArray(globalizedNode.modifiers)
                        ? globalizedNode.modifiers
                        : [globalizedNode.modifiers]),
                      ...(Array.isArray(nodeComplements.modifiers)
                        ? nodeComplements.modifiers
                        : [nodeComplements.modifiers]),
                    ]),
                  ],
                }
              : {})(),
        };

        const nodeModifiers = modifiers.reduce(
          (acc: any, { type, ...acv }: any) =>
            (Array.isArray(complementedNode?.modifiers)
              ? complementedNode?.modifiers
              : [complementedNode?.modifiers]
            ).includes(type)
              ? {
                  ...acc,
                  ...acv,
                }
              : acc,
          {},
        );
        const modifiedNode = {
          ...complementedNode,
          ...nodeModifiers,
        };
        return modifiedNode;
      })
      .filter(visibility(form))
      .map((node) => {
        const colorProp = node.properties?.find(({ type }) => type === 'color');
        if (colorProp) {
          return {
            ...node,
            ...((Array.isArray(colorProp.value)
              ? colorProp.value
              : [colorProp.value]
            ).includes(getNestedField(form, colorProp.key))
              ? {
                  ...colorProp.properties,
                }
              : {}),
          };
        }
        return node;
      })
      .map((node) =>
        node.properties
          ? {
              ...node,
              properties: node.properties.filter(
                ({ type }) => !['visibility', 'color'].includes(type),
              ),
            }
          : node,
      )
      .map(({ text, code, params, ...node }) => {
        return {
          ...node,
          text: dynamicField(form, text),
          ...(() => (code ? { code: dynamicField(form, code) } : {}))(),
          ...(() =>
            params
              ? {
                  params: (Array.isArray(params) ? params : [params]).map(
                    (param) => dynamicField(form, param),
                  ),
                }
              : {})(),
        };
      })
      .map(
        ({
          width: w = 0,
          height: h = 0,
          top = 0,
          left,
          right,
          type,
          text,
          properties,
          modifiers,
          size,
          ...node
        }) => {
          const factorX = format === 'html' ? axisX : width;
          const factorY = format === 'html' ? axisY : height;
          return {
            key: type,
            text,
            width: w * factorX,
            height: h * factorY,
            top: top * factorY,
            ...(() => (properties?.length ? { properties } : {}))(),
            ...(() =>
              size
                ? {
                    size: size * factorX,
                    width: size * factorX,
                    height: size * factorX,
                  }
                : {})(),
            ...(() =>
              ![undefined, null].includes(left)
                ? { left: left * factorX }
                : {})(),
            ...(() =>
              ![undefined, null].includes(right)
                ? { right: right * factorX }
                : {})(),
            ...node,
            matrix: defaultTransformMatrix,
          };
        },
      );
  };

export const getElements = (state: RootState) => (group: InterfaceGroup) => {
  const elements = getInterfaces(state)(group);
  const form = getShowSingleForm(state)(null);
  const interactNodes = (nodes: Interface[]) => {
    return nodes
      .filter(visibility(form))
      .map(({ children, ...node }) =>
        children ? { ...node, children: interactNodes(children) } : { ...node },
      );
  };

  return interactNodes(elements);
};

export const getShowSingleForm =
  (state: RootState) =>
  <T extends FlattenKeys<SingleBannerForm> | undefined | null | ''>(
    field: T,
  ): DeepType<SingleBannerForm, T> => {
    const {
      form: { getForm },
    } = rootGetters(state);
    const showSingleFormId = getForm('batchBanner.showSingleFormId');
    const singleForm =
      getForm('batchBanner.singleForms').find(
        ({ id }) => showSingleFormId === id,
      ) ?? getForm('batchBanner.defaultSingleForm');
    return getNestedField(singleForm, field);
  };

export const isBatchMode = (state: RootState) => () => {
  const {
    form: { getForm },
  } = rootGetters(state);
  const batchForm = getForm('batchBanner');
  return (
    batchForm.batch.pinnedSingleForms.length > 1 ||
    (batchForm.batch.pinnedSingleForms.length === 0 &&
      batchForm.singleForms.length > 1)
  );
};

const formGetters = (state: RootState) => ({
  getForm: getForm(state),
  getBlurForm: getBlurForm(state),
  getBannerConfigurations: getBannerConfigurations(state),
  getRecoverFormMessage: getRecoverFormMessage(state),
  getTransformations: getTransformations(state),
  getElements: getElements(state),
  getShowSingleForm: getShowSingleForm(state),
  isBatchMode: isBatchMode(state),
});

export default formGetters;
